import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { RefreshCcw, Trash2, Upload } from "lucide-react";
import api from "../../../../services/api";
import { useAuth } from "../../../../hooks/use-auth";
import { useToast } from "../../../../contexts/toast/toast-context";
import { getApiErrorMessage } from "../../../../utils";
import type { Tenant } from "../../../../types/tenant";
import { BackButton } from "../../../../components/button/back-button";
import { LoadingButton } from "../../../../components/button/loading-button";
import { ConfirmationModal } from "../../../../components/confirmation-modal";
import {
  APP_RESOURCES,
  AccessControl,
} from "../../../../permissions/access-control";
import type { AiModel } from "../../../../types/ai-model";
import { FileUpload } from "../../../../components/file-upload";

const MAX_RAG_FILE_SIZE_BYTES = 20 * 1024 * 1024;

type FindAllTenantDto = {
  data: Tenant[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

type RagDocument = {
  key: string;
  fileName: string;
  size: number;
  lastModified?: string;
};

type ModelOption = {
  value: string;
  label: string;
};

type RagTenantConfig = Pick<
  Tenant,
  | "appName"
  | "enabledAi"
  | "enabledRag"
  | "queryRewriteContextModel"
  | "gptEmbeddingModel"
>;

const formatFileSize = (value: number): string => {
  const size = Number(value);
  if (!Number.isFinite(size) || size <= 0) return "0 B";

  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024)
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const formatDateTime = (value?: string): string => {
  if (!value) return "-";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";

  return parsed.toLocaleString("pt-BR");
};

export default function TenantRagConfig() {
  const { user } = useAuth();
  const { error, success, warn } = useToast();

  const [tenant, setTenant] = useState<RagTenantConfig | null>(null);
  const [isLoadingTenant, setIsLoadingTenant] = useState(true);

  const [documents, setDocuments] = useState<RagDocument[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [pendingUploadFiles, setPendingUploadFiles] = useState<File[]>([]);

  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSavingModels, setIsSavingModels] = useState(false);
  const [aiModelOptions, setAiModelOptions] = useState<ModelOption[]>([]);
  const [isLoadingAiModels, setIsLoadingAiModels] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(true);
  const [selectedTenantAppName, setSelectedTenantAppName] = useState("");

  const canAccess = useMemo(
    () => AccessControl.canView(APP_RESOURCES.CONFIG_RAG_TENANT, user?.role),
    [user?.role],
  );

  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const allSelected =
    documents.length > 0 &&
    documents.every((document) => selectedSet.has(document.key));
  const availableTenantAppNames = useMemo(
    () =>
      Array.from(
        new Set(
          tenants
            .map((item) => item.appName?.trim())
            .filter((appName): appName is string => !!appName),
        ),
      ).sort((left, right) => left.localeCompare(right, "pt-BR")),
    [tenants],
  );
  const aiModelOptionsWithSelected = useMemo(() => {
    const options = [...aiModelOptions];
    const existing = new Set(options.map((option) => option.value));

    [tenant?.queryRewriteContextModel, tenant?.gptEmbeddingModel].forEach(
      (model) => {
        const normalized = model?.trim();
        if (!normalized || existing.has(normalized)) return;
        existing.add(normalized);
        options.push({ value: normalized, label: normalized });
      },
    );

    return options;
  }, [
    aiModelOptions,
    tenant?.gptEmbeddingModel,
    tenant?.queryRewriteContextModel,
  ]);

  useEffect(() => {
    if (!canAccess) return;

    let isMounted = true;
    const fetchAiModels = async () => {
      try {
        setIsLoadingAiModels(true);
        const response = await api.get<AiModel[]>("/ai-models");
        if (!isMounted) return;
        setAiModelOptions(
          (response.data ?? [])
            .filter(
              (item) => typeof item?.model === "string" && !!item.model.trim(),
            )
            .map((item) => ({
              value: item.model.trim(),
              label: item.vendor?.trim()
                ? `${item.model.trim()} · ${item.vendor.trim()}`
                : item.model.trim(),
            })),
        );
      } catch (err) {
        if (!isMounted) return;
        setAiModelOptions([]);
        error(getApiErrorMessage(err));
      } finally {
        if (isMounted) setIsLoadingAiModels(false);
      }
    };

    void fetchAiModels();
    return () => {
      isMounted = false;
    };
  }, [canAccess, error]);

  const loadDocuments = async (appName: string) => {
    try {
      setIsLoadingDocuments(true);
      const response = await api.get<RagDocument[]>(
        `/rag/documents/${appName}`,
      );
      const nextDocuments = Array.isArray(response.data) ? response.data : [];
      setDocuments(nextDocuments);
      setSelectedKeys((previous) =>
        previous.filter((key) =>
          nextDocuments.some((document) => document.key === key),
        ),
      );
    } catch (err) {
      error(getApiErrorMessage(err));
      setDocuments([]);
      setSelectedKeys([]);
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  useEffect(() => {
    if (!canAccess) {
      setTenants([]);
      setSelectedTenantAppName("");
      setIsLoadingTenants(false);
      return;
    }

    let isMounted = true;

    const fetchTenants = async () => {
      try {
        setIsLoadingTenants(true);

        const pageSize = 100;
        let pageNumber = 1;
        let totalPages = 1;
        const allTenants: Tenant[] = [];

        while (pageNumber <= totalPages) {
          const response = await api.get<FindAllTenantDto>("/tenant", {
            params: { page: pageNumber, limit: pageSize },
          });

          allTenants.push(...response.data.data);
          totalPages = response.data.totalPages;
          pageNumber += 1;
        }

        const uniqueTenants = Array.from(
          new Map(allTenants.map((item) => [item.appName, item])).values(),
        );

        if (!isMounted) return;
        setTenants(uniqueTenants);
        setSelectedTenantAppName((previous) => {
          if (
            previous &&
            uniqueTenants.some((item) => item.appName === previous)
          ) {
            return previous;
          }

          if (
            user?.appName &&
            uniqueTenants.some((item) => item.appName === user.appName)
          ) {
            return user.appName;
          }

          return uniqueTenants[0]?.appName ?? "";
        });
      } catch (err) {
        if (!isMounted) return;
        error(getApiErrorMessage(err));
        setTenants([]);
        setSelectedTenantAppName("");
      } finally {
        if (!isMounted) return;
        setIsLoadingTenants(false);
      }
    };

    void fetchTenants();

    return () => {
      isMounted = false;
    };
  }, [canAccess, error, user?.appName]);

  useEffect(() => {
    if (!selectedTenantAppName) {
      setTenant(null);
      setDocuments([]);
      setSelectedKeys([]);
      setPendingUploadFiles([]);
      setIsLoadingTenant(false);
      return;
    }

    const loadTenant = async () => {
      try {
        setIsLoadingTenant(true);
        setDocuments([]);
        setSelectedKeys([]);
        setPendingUploadFiles([]);

        const response = await api.get<Tenant>(
          `/tenant/${selectedTenantAppName}`,
        );
        const tenantConfig = {
          appName: response.data.appName,
          enabledAi: !!response.data.enabledAi,
          enabledRag: !!response.data.enabledRag,
          queryRewriteContextModel:
            response.data.queryRewriteContextModel ?? "gpt-5-nano",
          gptEmbeddingModel:
            response.data.gptEmbeddingModel ?? "text-embedding-3-small",
        };
        setTenant(tenantConfig);

        if (tenantConfig.enabledAi && tenantConfig.enabledRag) {
          await loadDocuments(tenantConfig.appName);
        }
      } catch (err) {
        error(getApiErrorMessage(err));
        setTenant(null);
        setDocuments([]);
        setSelectedKeys([]);
      } finally {
        setIsLoadingTenant(false);
      }
    };

    void loadTenant();
  }, [selectedTenantAppName, error]);

  const handleToggleKey = (key: string) => {
    setSelectedKeys((previous) =>
      previous.includes(key)
        ? previous.filter((item) => item !== key)
        : [...previous, key],
    );
  };

  const handleToggleAll = (checked: boolean) => {
    if (checked) {
      setSelectedKeys(documents.map((document) => document.key));
      return;
    }

    setSelectedKeys([]);
  };

  const syncEmbeddings = async () => {
    if (!tenant?.appName || isSyncing) return;

    try {
      setIsSyncing(true);
      await api.post(`/rag/embeddings/${tenant.appName}`);
      success("Sincronização do RAG iniciada!");
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setIsSyncing(false);
    }
  };

  const saveModels = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!tenant?.appName || isSavingModels) return;

    try {
      setIsSavingModels(true);
      await api.patch(`/tenant/${tenant.appName}`, {
        queryRewriteContextModel: tenant.queryRewriteContextModel,
        gptEmbeddingModel: tenant.gptEmbeddingModel,
      });
      success("Modelos do RAG atualizados com sucesso!");
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setIsSavingModels(false);
    }
  };

  const uploadFiles = async () => {
    if (!tenant?.appName || isUploading) return;

    if (pendingUploadFiles.length === 0) {
      warn("Selecione ao menos um arquivo para upload.");
      return;
    }

    try {
      setIsUploading(true);

      const formData = new FormData();
      pendingUploadFiles.forEach((file) => {
        formData.append("files", file);
      });

      await api.post(`/rag/documents/${tenant.appName}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      success("Upload de documentos concluído.");
      setPendingUploadFiles([]);
      await loadDocuments(tenant.appName);
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setIsUploading(false);
    }
  };

  const deleteSelectedDocuments = async () => {
    if (!tenant?.appName || isDeleting || selectedKeys.length === 0) return;

    try {
      setIsDeleting(true);
      await api.delete(`/rag/documents/${tenant.appName}`, {
        data: { keys: selectedKeys },
      });
      success("Documentos removidos com sucesso.");
      setIsDeleteModalOpen(false);
      setSelectedKeys([]);
      await loadDocuments(tenant.appName);
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setIsDeleting(false);
    }
  };

  const tenantSelector = (
    <div className="max-w-md">
      <label className="form-label">Tenant</label>
      <select
        className="form-input"
        value={selectedTenantAppName}
        onChange={(event) => setSelectedTenantAppName(event.target.value)}
        disabled={
          isLoadingTenants ||
          isLoadingTenant ||
          isSyncing ||
          isUploading ||
          isDeleting ||
          isSavingModels
        }
      >
        {isLoadingTenants && <option value="">Carregando...</option>}
        {!isLoadingTenants && availableTenantAppNames.length === 0 && (
          <option value="">Nenhum tenant disponível</option>
        )}
        {!isLoadingTenants &&
          availableTenantAppNames.map((appName) => (
            <option key={appName} value={appName}>
              {appName}
            </option>
          ))}
      </select>
    </div>
  );

  if (!canAccess) {
    return (
      <div className="app-page">
        <BackButton />
        <h1 className="app-page-title mb-2">Configurações de RAG</h1>
        <p className="app-page-subtitle">
          Você não tem permissão para acessar esta página.
        </p>
      </div>
    );
  }

  if (isLoadingTenants || isLoadingTenant) {
    return (
      <div className="app-page">
        <BackButton />
        <h1 className="app-page-title mb-6">Configurações de RAG</h1>
        {tenantSelector}
        <p className="app-page-subtitle">Carregando configurações...</p>
      </div>
    );
  }

  if (!selectedTenantAppName) {
    return (
      <div className="app-page">
        <BackButton />
        <h1 className="app-page-title mb-6">Configurações de RAG</h1>
        {tenantSelector}
        <p className="app-page-subtitle mt-4">
          Nenhum tenant disponível para edição.
        </p>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="app-page">
        <BackButton />
        <h1 className="app-page-title mb-6">Configurações de RAG</h1>
        {tenantSelector}
        <p className="app-page-subtitle">
          Não foi possível carregar as configurações do tenant.
        </p>
      </div>
    );
  }

  if (!tenant.enabledAi) {
    return (
      <div className="app-page">
        <BackButton />
        <h1 className="app-page-title mb-6">Configurações de RAG</h1>
        {tenantSelector}
        <p className="app-page-subtitle mt-4">
          A Inteligência Artificial está desabilitada para este tenant.
        </p>
      </div>
    );
  }

  if (!tenant.enabledRag) {
    return (
      <div className="app-page">
        <BackButton />
        <h1 className="app-page-title mb-6">Configurações de RAG</h1>
        {tenantSelector}
        <p className="app-page-subtitle mt-4">
          O RAG está desabilitado para este tenant.
        </p>
      </div>
    );
  }

  return (
    <div className="app-page space-y-6">
      <BackButton />
      <h1 className="app-page-title">Configurações de RAG</h1>
      {tenantSelector}

      <form onSubmit={saveModels} className="app-card p-4 md:p-6 space-y-4">
        <div>
          <h2 className="app-section-title">Modelos</h2>
          <p className="app-page-subtitle">
            Defina os modelos usados na preparação do contexto e na geração dos
            embeddings.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="form-label">Modelo para contexto RAG</label>
            <Select<ModelOption, false>
              name="queryRewriteContextModel"
              options={aiModelOptionsWithSelected}
              value={
                tenant.queryRewriteContextModel
                  ? {
                      value: tenant.queryRewriteContextModel,
                      label: tenant.queryRewriteContextModel,
                    }
                  : null
              }
              onChange={(option) =>
                setTenant((previous) =>
                  previous
                    ? {
                        ...previous,
                        queryRewriteContextModel: option?.value ?? "",
                      }
                    : previous,
                )
              }
              isLoading={isLoadingAiModels}
              isSearchable
              className="text-sm"
              classNamePrefix="ai-model-select"
              placeholder="Selecione o modelo de contexto..."
              noOptionsMessage={() => "Nenhum modelo encontrado"}
            />
          </div>

          <div>
            <label className="form-label">Modelo para embeddings</label>
            <Select<ModelOption, false>
              name="gptEmbeddingModel"
              options={aiModelOptionsWithSelected}
              value={
                tenant.gptEmbeddingModel
                  ? {
                      value: tenant.gptEmbeddingModel,
                      label: tenant.gptEmbeddingModel,
                    }
                  : null
              }
              onChange={(option) =>
                setTenant((previous) =>
                  previous
                    ? { ...previous, gptEmbeddingModel: option?.value ?? "" }
                    : previous,
                )
              }
              isLoading={isLoadingAiModels}
              isSearchable
              className="text-sm"
              classNamePrefix="ai-model-select"
              placeholder="Selecione o modelo de embeddings..."
              noOptionsMessage={() => "Nenhum modelo encontrado"}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <LoadingButton
            type="submit"
            isLoading={isSavingModels}
            className="btn btn-primary"
            loadingLabel="Salvando"
          >
            Salvar modelos
          </LoadingButton>
        </div>
      </form>

      <section className="app-card p-4 md:p-6 space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="app-section-title">Sincronização</h2>
            <p className="app-page-subtitle">
              Inicia o processo assíncrono de atualização dos embeddings.
            </p>
          </div>

          <LoadingButton
            type="button"
            onClick={syncEmbeddings}
            isLoading={isSyncing}
            className="btn btn-secondary"
            loadingLabel="Sincronizando"
          >
            <RefreshCcw className="h-4 w-4" />
            Sincronizar arquivos RAG
          </LoadingButton>
        </div>
      </section>

      <section className="app-card p-4 md:p-6 space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="app-section-title">Documentos no S3</h2>
            <p className="app-page-subtitle">
              Faça upload de arquivos PDF, TXT, MD ou CSV para uso no RAG.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => loadDocuments(tenant.appName)}
              className="btn btn-secondary"
              disabled={isLoadingDocuments}
            >
              <RefreshCcw className="h-4 w-4" />
              Atualizar
            </button>

            <LoadingButton
              type="button"
              onClick={() => setIsDeleteModalOpen(true)}
              className="btn btn-danger"
              isLoading={isDeleting}
              disabled={selectedKeys.length === 0}
              loadingLabel="Excluindo"
            >
              <Trash2 className="h-4 w-4" />
              Excluir selecionados
            </LoadingButton>
          </div>
        </div>

        <FileUpload files={pendingUploadFiles} onFilesChange={setPendingUploadFiles} accept=".pdf,.txt,.md,.csv" acceptLabel="PDF, TXT, MD ou CSV" maxFiles={20} maxSizeBytes={MAX_RAG_FILE_SIZE_BYTES} uploading={isUploading} disabled={isLoadingDocuments || isDeleting} onValidationError={error} emptyLabel="Nenhum documento selecionado para upload." />
        <div className="flex justify-end">
          <LoadingButton
            type="button"
            onClick={uploadFiles}
            className="btn btn-primary"
            isLoading={isUploading}
            loadingLabel="Enviando"
          >
            <Upload className="h-4 w-4" />
            Upload
          </LoadingButton>
        </div>

        <div className="w-full overflow-x-auto">
          <table className="w-full table-auto divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-500 w-12">
                  <input
                    type="checkbox"
                    className="form-checkbox"
                    checked={allSelected}
                    onChange={(event) => handleToggleAll(event.target.checked)}
                    disabled={documents.length === 0}
                    aria-label="Selecionar todos os documentos"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Arquivo
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Tamanho
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Atualizado em
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {isLoadingDocuments && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-4 text-center text-sm text-slate-500"
                  >
                    Carregando documentos...
                  </td>
                </tr>
              )}

              {!isLoadingDocuments && documents.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-4 text-center text-sm text-slate-500"
                  >
                    Nenhum documento encontrado na pasta do tenant.
                  </td>
                </tr>
              )}

              {!isLoadingDocuments &&
                documents.map((document) => (
                  <tr
                    key={document.key}
                    className="transition hover:bg-slate-50/70"
                  >
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        className="form-checkbox"
                        checked={selectedSet.has(document.key)}
                        onChange={() => handleToggleKey(document.key)}
                        aria-label={`Selecionar ${document.fileName}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 break-all">
                      {document.fileName}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      {formatFileSize(document.size)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      {formatDateTime(document.lastModified)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        title="Confirmar exclusão"
        description={`Deseja excluir ${selectedKeys.length} documento(s) selecionado(s)?`}
        onCancel={() => {
          if (!isDeleting) {
            setIsDeleteModalOpen(false);
          }
        }}
        onConfirm={deleteSelectedDocuments}
        isLoading={isDeleting}
        confirmLabel="Excluir"
      />
    </div>
  );
}
